/**
 * RunningBillPublicView.jsx  (iter-326c)
 *
 * Public, no-auth landing page for a client running bill.
 * Loaded via /public/running-bill/:token.
 *   - shows the bill items + cascade totals (read-only)
 *   - if status === 'sent', presents Accept CTA
 *   - on accept, calls POST /api/public/running-bill/:token/accept and shows
 *     a success state. The owner gets notified and a payment_request is
 *     auto-created.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');

export default function RunningBillPublicView() {
  const { token } = useParams();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [clientName, setClientName] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/public/running-bill/${token}`);
        setBill(res.data);
        setClientName(res.data?.client_name || '');
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Bill not found or link expired');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleAccept = async () => {
    if (!clientName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!window.confirm(`Accept Running Bill ${bill.bill_number} for ${fmt(bill.grand_total)}?\n\nThis confirms that the work described has been completed to your satisfaction and the amount is approved for billing.`)) return;
    setAccepting(true);
    try {
      const res = await axios.post(`${API}/public/running-bill/${token}/accept`, {
        client_name: clientName.trim(),
        notes: notes.trim(),
      });
      setBill(res.data?.bill);
      toast.success(res.data?.message || 'Bill accepted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-stone-400" size={40} />
      </div>
    );
  }
  if (!bill) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="text-center text-stone-500">
          <p className="text-lg font-semibold">Bill not found</p>
          <p className="text-sm mt-2">This link may have expired. Please contact your project owner.</p>
        </div>
      </div>
    );
  }

  const isAccepted = bill.status === 'accepted';

  return (
    <div className="min-h-screen bg-stone-50 py-8 px-4" data-testid="running-bill-public-view">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-stone-900 text-amber-300 px-6 py-5">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="text-[11px] tracking-[2px] uppercase text-stone-400">Running Bill</div>
              <div className="text-3xl font-bold mt-0.5">{bill.bill_number}</div>
              <div className="text-sm text-amber-200 mt-1">{bill.project_name} · {bill.simple_progress_pct}% complete</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase text-stone-400">Grand Total</div>
              <div className="text-3xl font-bold text-amber-300" data-testid="public-rb-grand-total">{fmt(bill.grand_total)}</div>
            </div>
          </div>
          {isAccepted && (
            <div className="mt-4 flex items-center gap-2 text-emerald-300 text-sm">
              <CheckCircle2 size={16} />
              <span>Accepted by <b>{bill.accepted_by_name}</b> on {new Date(bill.accepted_at).toLocaleString('en-IN')}</span>
            </div>
          )}
        </div>

        {/* Items */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-100 border-b">
              <tr>
                <th className="px-3 py-2 text-left text-xs uppercase font-semibold text-stone-600">#</th>
                <th className="px-3 py-2 text-left text-xs uppercase font-semibold text-stone-600">Item</th>
                <th className="px-3 py-2 text-right text-xs uppercase font-semibold text-stone-600">Actual Qty</th>
                <th className="px-3 py-2 text-right text-xs uppercase font-semibold text-stone-600">Actual Amt</th>
                <th className="px-3 py-2 text-center text-xs uppercase font-semibold text-stone-600">Progress</th>
                <th className="px-3 py-2 text-right text-xs uppercase font-bold text-emerald-700 bg-emerald-100">Billable So Far</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let itemNum = 0;
                return (bill.items || []).map((it, idx) => {
                  const isHeader = it.item_type === 'header' || it.is_header;
                  if (isHeader) {
                    return (
                      <tr key={idx} className="bg-amber-50 border-y border-amber-200">
                        <td colSpan={6} className="px-3 py-2 font-bold text-amber-900 text-xs uppercase tracking-wide">
                          {it.task_title || it.description || 'Section'}
                        </td>
                      </tr>
                    );
                  }
                  itemNum += 1;
                  return (
                <tr key={idx} className="border-b border-stone-100">
                  <td className="px-3 py-2 text-stone-500">{itemNum}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{it.task_title}</div>
                    {it.description && <div className="text-[11px] text-stone-500">{(it.description || '').replace(/<[^>]*>/g, '').slice(0, 120)}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">{it.actual_quantity}</td>
                  <td className="px-3 py-2 text-right">{fmt(it.actual_total)}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[11px] font-semibold border px-2 py-0.5 rounded-full ${(it.task_progress || 0) > 0 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-stone-400 bg-stone-50 border-stone-200'}`}>{it.task_progress || 0}%</span>
                  </td>
                  <td className={`px-3 py-2 text-right font-bold bg-emerald-50/60 ${(Number(it.billable_amount) || 0) > 0 ? 'text-emerald-800' : 'text-stone-300'}`}>{fmt(it.billable_amount)}</td>
                </tr>
                  );
                });
              })()}
            </tbody>
            <tfoot className="bg-stone-50 border-t-2 border-stone-300">
              <tr className="bg-emerald-100">
                <td colSpan={5} className="px-3 py-2 text-right font-bold text-emerald-900">Basic Total (Billable):</td>
                <td className="px-3 py-2 text-right font-bold text-emerald-900 bg-emerald-200">{fmt(bill.billable_basic_total)}</td>
              </tr>
              {Number(bill.discount_amount) > 0 && (
                <tr><td colSpan={5} className="px-3 py-2 text-right">Discount:</td><td className="px-3 py-2 text-right text-red-600">-{fmt(bill.discount_amount)}</td></tr>
              )}
              {Number(bill.overhead_freight) > 0 && (
                <tr><td colSpan={5} className="px-3 py-2 text-right">Freight:</td><td className="px-3 py-2 text-right">{fmt(bill.overhead_freight)}</td></tr>
              )}
              {Number(bill.overhead_labour) > 0 && (
                <tr><td colSpan={5} className="px-3 py-2 text-right">Labour Handling:</td><td className="px-3 py-2 text-right">{fmt(bill.overhead_labour)}</td></tr>
              )}
              {Number(bill.overhead_consultancy) > 0 && (
                <tr><td colSpan={5} className="px-3 py-2 text-right">Consultancy:</td><td className="px-3 py-2 text-right">{fmt(bill.overhead_consultancy)}</td></tr>
              )}
              {Number(bill.gst_amount) > 0 && (
                <tr><td colSpan={5} className="px-3 py-2 text-right">GST:</td><td className="px-3 py-2 text-right">{fmt(bill.gst_amount)}</td></tr>
              )}
              <tr className="bg-stone-900 text-white">
                <td colSpan={5} className="px-3 py-3 text-right font-bold text-base">GRAND TOTAL:</td>
                <td className="px-3 py-3 text-right font-bold text-amber-300 text-base">{fmt(bill.grand_total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Notes from owner */}
        {(bill.notes || '').trim() && (
          <div className="px-6 py-4 bg-stone-50 border-t border-stone-200">
            <div className="text-xs uppercase text-stone-500 font-semibold mb-1">Note from project owner</div>
            <div className="text-sm text-stone-700 whitespace-pre-wrap">{bill.notes}</div>
          </div>
        )}

        {/* Accept CTA */}
        {!isAccepted && bill.status === 'sent' && (
          <div className="px-6 py-6 bg-emerald-50 border-t-2 border-emerald-200" data-testid="public-rb-accept-block">
            <div className="text-lg font-bold text-emerald-900 mb-1">Accept this Running Bill</div>
            <p className="text-sm text-emerald-800 mb-4">By accepting, you confirm the work described above is complete and the amount is approved for billing. A payment request will be created automatically.</p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs uppercase text-stone-600 font-semibold">Your Name *</label>
                <Input
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Your full name"
                  data-testid="public-rb-name-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase text-stone-600 font-semibold">Notes (optional)</label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any comments…"
                  rows={2}
                  data-testid="public-rb-notes-input"
                />
              </div>
            </div>
            <Button
              size="lg"
              onClick={handleAccept}
              disabled={accepting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
              data-testid="public-rb-accept-btn"
            >
              {accepting ? <Loader2 className="animate-spin mr-2" size={16} /> : <CheckCircle2 size={16} className="mr-2" />}
              {accepting ? 'Accepting…' : `Accept & Approve ${fmt(bill.grand_total)}`}
            </Button>
          </div>
        )}

        {isAccepted && (
          <div className="px-6 py-6 bg-emerald-50 border-t-2 border-emerald-300 text-center" data-testid="public-rb-accepted-state">
            <CheckCircle2 className="mx-auto text-emerald-600 mb-2" size={48} />
            <div className="text-xl font-bold text-emerald-900">Bill Accepted</div>
            <div className="text-sm text-emerald-800 mt-1">Thank you. The project owner has been notified and a payment request has been created.</div>
            {bill.client_acceptance_notes && (
              <div className="mt-3 text-sm text-stone-600 italic">"{bill.client_acceptance_notes}"</div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-3 bg-stone-100 text-[11px] text-stone-500 text-center border-t">
          Running Bill {bill.bill_number} · Generated {(bill.created_at || '').slice(0, 10)} · Bella Casa Interiors
        </div>
      </div>
    </div>
  );
}
